import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Dropdown,
} from "react-bootstrap";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import debounce from "lodash/debounce";
import Spinner from "../components/Common/Spinner";
import Pagination from "../components/Pagination";
import {
  getSubscriptionPlans,
  createSubscriptionPlan,
  getSubscriptionPlanDetails,
  updateSubscriptionPlan,
  deleteSubscriptionPlan,
} from "../../services/Admin/adminServices";

const Subscriptions = () => {
  // Data state
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);

  // Pagination & Search
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreatedSuccessModal, setShowCreatedSuccessModal] = useState(false);

  const [selectedPlan, setSelectedPlan] = useState(null);
  const [createdPlan, setCreatedPlan] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Feature tag input temporary state
  const [featureInput, setFeatureInput] = useState("");

  const svg1 = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="12" cy="5" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="19" r="2"></circle>
      </g>
    </svg>
  );

  // Fetch plans
  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getSubscriptionPlans(currentPage, itemsPerPage, debouncedSearch);
      const resData = res?.data?.data;
      setPlans(resData?.subscriptionPlans || []);
      setTotalItems(resData?.total || 0);
    } catch (error) {
      console.error("Error fetching subscription plans:", error);
      const message = error?.response?.data?.message || "Failed to fetch subscription plans";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, debouncedSearch]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  // Handle Search Debounce
  useEffect(() => {
    setCurrentPage(0);
    const handler = debounce(() => {
      setDebouncedSearch(search);
    }, 500);

    handler();
    return () => {
      handler.cancel();
    };
  }, [search]);

  // View Details
  const handleViewDetails = async (plan) => {
    setSelectedPlan(plan);
    setShowDetailsModal(true);
    if (plan?._id) {
      try {
        setDetailsLoading(true);
        const res = await getSubscriptionPlanDetails(plan._id);
        if (res?.data?.data) {
          setSelectedPlan(res.data.data);
        }
      } catch (error) {
        console.error("Error loading plan details:", error);
      } finally {
        setDetailsLoading(false);
      }
    }
  };

  // Open Edit
  const handleOpenEdit = (plan) => {
    setSelectedPlan(plan);
    setShowEditModal(true);
  };

  // Open Delete Confirmation
  const handleOpenDelete = (plan) => {
    setSelectedPlan(plan);
    setShowDeleteModal(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!selectedPlan?._id) return;
    try {
      setSubmitting(true);
      const res = await deleteSubscriptionPlan(selectedPlan._id);
      const msg = res?.data?.message || "Subscription plan disabled successfully";
      toast.success(msg);
      setShowDeleteModal(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (error) {
      console.error("Error deleting plan:", error);
      const msg = error?.response?.data?.message || "Failed to disable subscription plan";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Quick Toggle Active
  const handleToggleActive = async (plan) => {
    try {
      await updateSubscriptionPlan({
        subscriptionPlanId: plan._id,
        isActive: !plan.isActive,
      });
      toast.success(
        `Plan ${!plan.isActive ? "activated" : "deactivated"} successfully`
      );
      fetchPlans();
    } catch (error) {
      console.error("Error toggling plan status:", error);
      const msg = error?.response?.data?.message || "Failed to update status";
      toast.error(msg);
    }
  };

  // Copy Razorpay ID
  const handleCopyRazorpayId = (razorpayId) => {
    if (razorpayId) {
      navigator.clipboard.writeText(razorpayId);
      toast.success("Razorpay Plan ID copied to clipboard!");
    }
  };

  // Validation Schemas
  const createValidationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be at most 50 characters")
      .required("Plan name is required"),
    description: Yup.string().trim(),
    amount: Yup.number()
      .typeError("Must be number")
      .min(1, "Amount must be at least ₹1")
      .required("Amount is required"),
    currency: Yup.string().default("INR"),
    period: Yup.string()
      .oneOf(["daily", "weekly", "monthly", "yearly"], "Invalid period")
      .required("Period is required"),
    interval: Yup.number()
      .typeError("Must be number")
      .min(1, "Interval must be at least 1")
      .required("Interval is required"),
    features: Yup.array().of(Yup.string()),
    isPopular: Yup.boolean().default(false),
  });

  const editValidationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be at most 50 characters")
      .required("Plan name is required"),
    description: Yup.string().trim(),
    features: Yup.array().of(Yup.string()),
    isPopular: Yup.boolean(),
    isActive: Yup.boolean(),
  });

  // Handle Create Submit
  const handleCreateSubmit = async (values, { resetForm }) => {
    try {
      setSubmitting(true);
      const payload = {
        name: values.name.trim(),
        description: values.description ? values.description.trim() : "",
        amount: Number(values.amount),
        currency: values.currency || "INR",
        period: values.period,
        interval: Number(values.interval),
        features: values.features || [],
        isPopular: Boolean(values.isPopular),
      };

      const res = await createSubscriptionPlan(payload);
      if (res?.data?.statusCode === 201 || res?.status === 201 || res?.data?.data) {
        const createdData = res?.data?.data;
        toast.success(res?.data?.message || "Subscription plan created successfully");
        resetForm();
        setShowCreateModal(false);
        setCreatedPlan(createdData);
        setShowCreatedSuccessModal(true);
        fetchPlans();
      }
    } catch (error) {
      console.error("Error creating plan:", error);
      const status = error?.response?.status;
      const msg = error?.response?.data?.message || "Failed to create subscription plan";

      if (status === 409) {
        toast.error("Subscription plan already exists with this name. Please change the name.");
      } else if (msg.includes("Razorpay is not configured")) {
        toast.error("Razorpay is not configured on server (missing API keys).");
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Handle Edit Submit
  const handleEditSubmit = async (values) => {
    try {
      setSubmitting(true);
      const payload = {
        subscriptionPlanId: selectedPlan._id,
        name: values.name.trim(),
        description: values.description ? values.description.trim() : "",
        features: values.features || [],
        isPopular: Boolean(values.isPopular),
        isActive: Boolean(values.isActive),
      };

      const res = await updateSubscriptionPlan(payload);
      toast.success(res?.data?.message || "Subscription plan updated successfully");
      setShowEditModal(false);
      setSelectedPlan(null);
      fetchPlans();
    } catch (error) {
      console.error("Error updating plan:", error);
      const msg = error?.response?.data?.message || "Failed to update subscription plan";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="container mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h1 style={{ color: "#87C55F" }}>Subscription Plans</h1>
        <Button
          style={{ backgroundColor: "#87C55F", border: "none" }}
          onClick={() => setShowCreateModal(true)}
        >
          Add Subscription
        </Button>
      </div>

      {/* Search Input Bar matching Mentor/Subjects */}
      <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 align-items-end mb-4">
        <div className="flex-grow-1">
          <input
            type="text"
            style={{
              height: "44px",
              borderRadius: "0.5rem",
              borderColor: "#87C55F",
              boxShadow: "0 2px 6px rgba(135, 197, 95, 0.12)",
            }}
            className="form-control w-100"
            placeholder="Search plan by name....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {search && (
          <div>
            <button
              type="button"
              className="btn bg-danger text-white"
              style={{ borderRadius: "0.5rem", height: "44px" }}
              onClick={() => setSearch("")}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <Spinner />
      ) : (
        <div className="table-responsive" style={{ overflowX: "auto" }}>
          <table className="table table-striped align-middle shadow-sm">
            <thead className="table-light">
              <tr>
                <th>Sr No.</th>
                <th>Plan Name</th>
                <th>Amount</th>
                <th>Billing Cycle</th>
                <th>Popular</th>
                <th>Status</th>
                <th>Created Date</th>
                <th style={{ textAlign: "center" }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {plans?.length > 0 ? (
                plans.map((plan, index) => (
                  <tr key={plan._id || index}>
                    <td>{currentPage * itemsPerPage + index + 1}</td>
                    <td>
                      <div className="fw-bold">{plan.name}</div>
                      {plan.description && (
                        <small className="text-muted d-block">{plan.description}</small>
                      )}
                    </td>
                    <td>
                      <span style={{ color: "#87C55F", fontWeight: "600" }}>
                        ₹{plan.amount}
                      </span>{" "}
                      <small className="text-muted">{plan.currency || "INR"}</small>
                    </td>
                    <td>
                      Every {plan.interval > 1 ? plan.interval : ""} {plan.period}
                    </td>
                    <td>
                      {plan.isPopular ? (
                        <span className="badge bg-warning text-white">Popular</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Check
                          type="switch"
                          id={`switch-${plan._id}`}
                          checked={plan.isActive}
                          onChange={() => handleToggleActive(plan)}
                        />
                        <span
                          className={`badge ${plan.isActive ? "bg-success" : "bg-secondary"}`}
                        >
                          {plan.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </td>
                    <td>
                      {plan.createdAt
                        ? new Date(plan.createdAt).toLocaleDateString("en-IN", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                        : "N/A"}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <Dropdown align="end">
                        <Dropdown.Toggle
                          variant="light"
                          id={`dropdown-basic-${index}`}
                          style={{ padding: "4px 8px" }}
                        >
                          {svg1}
                        </Dropdown.Toggle>

                        <Dropdown.Menu align="end">
                          <Dropdown.Item onClick={() => handleViewDetails(plan)}>
                            View Details
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleOpenEdit(plan)}>
                            Edit
                          </Dropdown.Item>
                          <Dropdown.Item onClick={() => handleOpenDelete(plan)}>
                            Delete
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center">
                    No subscription plans found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination Footer */}
      {plans?.length > 0 && (
        <div className="card-footer clearfix">
          <div className="d-flex justify-content-between align-items-center">
            <div className="dataTables_info" style={{ color: "#87C55F" }}>
              Showing {currentPage * itemsPerPage + 1} to{" "}
              {totalItems > (currentPage + 1) * itemsPerPage
                ? (currentPage + 1) * itemsPerPage
                : totalItems}{" "}
              of {totalItems} entries
            </div>
            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <Pagination
                pageCount={totalPages}
                pageValue={currentPage}
                setPage={setCurrentPage}
              />
            </div>
          </div>
        </div>
      )}

      {/* CREATE MODAL */}
      <Modal show={showCreateModal} onHide={() => !submitting && setShowCreateModal(false)} centered size="lg">
        <Modal.Header closeButton={!submitting}>
          <Modal.Title style={{ color: "#87C55F" }}>
            Add Subscription Plan
          </Modal.Title>
        </Modal.Header>

        <Formik
          initialValues={{
            name: "",
            description: "",
            amount: 199,
            currency: "INR",
            period: "monthly",
            interval: 1,
            features: [],
            isPopular: false,
          }}
          validationSchema={createValidationSchema}
          onSubmit={handleCreateSubmit}
        >
          {({ values, setFieldValue, errors, touched }) => {
            const addFeature = () => {
              if (featureInput.trim()) {
                if (!values.features.includes(featureInput.trim())) {
                  setFieldValue("features", [...values.features, featureInput.trim()]);
                }
                setFeatureInput("");
              }
            };

            const removeFeature = (idx) => {
              setFieldValue(
                "features",
                values.features.filter((_, i) => i !== idx)
              );
            };

            return (
              <FormikForm>
                <Modal.Body className="py-3 px-4">
                  <div className="row g-3">
                    {/* Row 1: Name & Amount */}
                    <div className="col-md-6 mb-1">
                      <label className="form-label fw-semibold text-dark mb-1">
                        Plan Name <span className="text-danger">*</span>
                      </label>
                      <Field
                        name="name"
                        placeholder="ex. Monthly, Yearly"
                        className={`form-control ${touched.name && errors.name ? "is-invalid" : ""}`}
                        style={{
                          height: "44px",
                          borderRadius: "8px",
                          borderColor: touched.name && errors.name ? "#dc3545" : "#87C55F",
                          boxShadow: "0 2px 6px rgba(135, 197, 95, 0.15)",
                        }}
                      />
                      <ErrorMessage name="name" component="div" className="text-danger small mt-1" />
                    </div>

                    <div className="col-md-6 mb-1">
                      <label className="form-label fw-semibold text-dark mb-1">
                        Amount (₹ Rupees) <span className="text-danger">*</span>
                      </label>
                      <Field
                        name="amount"
                        type="number"
                        min="1"
                        placeholder="ex. 199"
                        className={`form-control ${touched.amount && errors.amount ? "is-invalid" : ""}`}
                        style={{
                          height: "44px",
                          borderRadius: "8px",
                          borderColor: touched.amount && errors.amount ? "#dc3545" : "#87C55F",
                          boxShadow: "0 2px 6px rgba(135, 197, 95, 0.15)",
                        }}
                      />
                      <small className="text-muted d-block mt-1">
                        Amount in rupees. Backend sends paise to Razorpay.
                      </small>
                      <ErrorMessage name="amount" component="div" className="text-danger small mt-1" />
                    </div>

                    {/* Row 2: Period & Interval (Balanced 50/50) */}
                    <div className="col-md-6 mb-1">
                      <label className="form-label fw-semibold text-dark mb-1">
                        Period <span className="text-danger">*</span>
                      </label>
                      <Field
                        name="period"
                        as="select"
                        className={`form-select ${touched.period && errors.period ? "is-invalid" : ""}`}
                        style={{
                          height: "44px",
                          borderRadius: "8px",
                          borderColor: touched.period && errors.period ? "#dc3545" : "#87C55F",
                          boxShadow: "0 2px 6px rgba(135, 197, 95, 0.15)",
                        }}
                      >
                        <option value="daily">daily</option>
                        <option value="weekly">weekly</option>
                        <option value="monthly">monthly</option>
                        <option value="yearly">yearly</option>
                      </Field>
                      <ErrorMessage name="period" component="div" className="text-danger small mt-1" />
                    </div>

                    <div className="col-md-6 mb-1">
                      <label className="form-label fw-semibold text-dark mb-1">
                        Interval <span className="text-danger">*</span>
                      </label>
                      <Field
                        name="interval"
                        type="number"
                        min="1"
                        placeholder="1"
                        className={`form-control ${touched.interval && errors.interval ? "is-invalid" : ""}`}
                        style={{
                          height: "44px",
                          borderRadius: "8px",
                          borderColor: touched.interval && errors.interval ? "#dc3545" : "#87C55F",
                          boxShadow: "0 2px 6px rgba(135, 197, 95, 0.15)",
                        }}
                      />
                      <small className="text-muted d-block mt-1">
                        Combined with period (e.g. interval 1 + monthly = every 1 month)
                      </small>
                      <ErrorMessage name="interval" component="div" className="text-danger small mt-1" />
                    </div>

                    {/* Row 3: Description */}
                    <div className="col-md-12 mb-1">
                      <label className="form-label fw-semibold text-dark mb-1">Description</label>
                      <Field
                        name="description"
                        as="textarea"
                        rows={2}
                        placeholder="Brief description of the plan..."
                        className="form-control"
                        style={{
                          borderRadius: "8px",
                          borderColor: "#87C55F",
                          boxShadow: "0 2px 6px rgba(135, 197, 95, 0.15)",
                        }}
                      />
                    </div>

                    {/* Row 4: Features Input & Add Button */}
                    <div className="col-md-12 mb-1">
                      <label className="form-label fw-semibold text-dark mb-1">Features</label>
                      <div className="d-flex">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Add feature and hit Enter"
                          value={featureInput}
                          onChange={(e) => setFeatureInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              addFeature();
                            }
                          }}
                          style={{
                            height: "44px",
                            borderRadius: "8px 0 0 8px",
                            borderColor: "#87C55F",
                            boxShadow: "0 2px 6px rgba(135, 197, 95, 0.15)",
                          }}
                        />
                        <Button
                          type="button"
                          style={{
                            height: "44px",
                            borderRadius: "0 8px 8px 0",
                            backgroundColor: "#87C55F",
                            border: "none",
                            padding: "0 24px",
                            fontWeight: "600",
                            boxShadow: "0 2px 6px rgba(135, 197, 95, 0.25)",
                          }}
                          onClick={addFeature}
                        >
                          Add
                        </Button>
                      </div>

                      {values.features.length > 0 && (
                        <div className="d-flex flex-wrap gap-2 mt-2">
                          {values.features.map((feat, idx) => (
                            <span
                              key={idx}
                              className="badge p-2 d-flex align-items-center gap-2"
                              style={{
                                backgroundColor: "#87C55F",
                                color: "#fff",
                                fontSize: "13px",
                                borderRadius: "6px",
                                boxShadow: "0 2px 4px rgba(135, 197, 95, 0.2)",
                              }}
                            >
                              {feat}
                              <span
                                style={{ cursor: "pointer", fontSize: "16px", fontWeight: "bold" }}
                                onClick={() => removeFeature(idx)}
                              >
                                &times;
                              </span>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Row 5: Checkbox Container */}
                    <div className="col-md-12 mb-1">
                      <div
                        className="p-2 px-3 rounded d-flex align-items-center gap-2"
                        style={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #e0e0e0",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                          height: "44px",
                        }}
                      >
                        <Field
                          type="checkbox"
                          name="isPopular"
                          id="isPopularCheck"
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                            accentColor: "#87C55F",
                          }}
                        />
                        <label className="fw-semibold text-dark mb-0" htmlFor="isPopularCheck" style={{ cursor: "pointer", fontSize: "14px" }}>
                          Mark as Popular
                        </label>
                      </div>
                    </div>
                  </div>
                </Modal.Body>

                <Modal.Footer className="py-3 px-4">
                  <Button
                    type="submit"
                    disabled={submitting}
                    style={{
                      height: "44px",
                      backgroundColor: "#87C55F",
                      border: "none",
                      padding: "0 28px",
                      fontWeight: "600",
                      borderRadius: "8px",
                      boxShadow: "0 3px 8px rgba(135, 197, 95, 0.3)",
                    }}
                  >
                    Add Subscription
                  </Button>
                </Modal.Footer>
              </FormikForm>
            );
          }}
        </Formik>
      </Modal>

      {/* EDIT MODAL */}
      <Modal show={showEditModal} onHide={() => !submitting && setShowEditModal(false)} centered size="lg">
        <Modal.Header closeButton={!submitting}>
          <Modal.Title style={{ color: "#87C55F" }}>
            Edit Subscription Plan
          </Modal.Title>
        </Modal.Header>

        {selectedPlan && (
          <Formik
            initialValues={{
              name: selectedPlan.name || "",
              description: selectedPlan.description || "",
              features: selectedPlan.features || [],
              isPopular: Boolean(selectedPlan.isPopular),
              isActive: Boolean(selectedPlan.isActive),
            }}
            validationSchema={editValidationSchema}
            onSubmit={handleEditSubmit}
            enableReinitialize
          >
            {({ values, setFieldValue, errors, touched }) => {
              const addFeature = () => {
                if (featureInput.trim()) {
                  if (!values.features.includes(featureInput.trim())) {
                    setFieldValue("features", [...values.features, featureInput.trim()]);
                  }
                  setFeatureInput("");
                }
              };

              const removeFeature = (idx) => {
                setFieldValue(
                  "features",
                  values.features.filter((_, i) => i !== idx)
                );
              };

              return (
                <FormikForm>
                  <Modal.Body className="py-3 px-4">
                    <div className="alert alert-warning p-2 mb-3 small">
                      <strong>Razorpay Restriction:</strong> Amount, period, and interval cannot be modified after creation.
                    </div>

                    <div className="row g-2 p-3 mb-3 bg-light rounded text-center small border">
                      <div className="col-4">
                        <small className="text-black d-block">Price</small>
                        <strong style={{ color: "black" }} className="fs-6">
                          ₹{selectedPlan.amount} {selectedPlan.currency || "INR"}
                        </strong>
                      </div>
                      <div className="col-4">
                        <small className="text-black d-block">Billing Period</small>
                        <strong className="text-black" >
                          Every {selectedPlan.interval > 1 ? selectedPlan.interval : ""} {selectedPlan.period}
                        </strong>
                      </div>
                      <div className="col-4">
                        <small className="text-black d-block">Razorpay Plan ID</small>
                        <code className="small">{selectedPlan.razorpayPlanId || "N/A"}</code>
                      </div>
                    </div>

                    <div className="row g-3">
                      <div className="col-md-12 mb-1">
                        <label className="form-label fw-semibold text-dark mb-1">
                          Plan Name <span className="text-danger">*</span>
                        </label>
                        <Field
                          name="name"
                          className={`form-control ${touched.name && errors.name ? "is-invalid" : ""}`}
                          style={{
                            height: "44px",
                            borderRadius: "8px",
                            borderColor: touched.name && errors.name ? "#dc3545" : "#87C55F",
                            boxShadow: "0 2px 6px rgba(135, 197, 95, 0.15)",
                          }}
                        />
                        <ErrorMessage name="name" component="div" className="text-danger small mt-1" />
                      </div>

                      <div className="col-12 mb-1">
                        <label className="form-label fw-semibold text-dark mb-1">Description</label>
                        <Field
                          name="description"
                          as="textarea"
                          rows={2}
                          className="form-control"
                          style={{
                            borderRadius: "8px",
                            borderColor: "#87C55F",
                            boxShadow: "0 2px 6px rgba(135, 197, 95, 0.15)",
                          }}
                        />
                      </div>

                      <div className="col-12 mb-1">
                        <label className="form-label fw-semibold text-dark mb-1">Features</label>
                        <div className="d-flex">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Add feature and hit Enter"
                            value={featureInput}
                            onChange={(e) => setFeatureInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addFeature();
                              }
                            }}
                            style={{
                              height: "44px",
                              borderRadius: "8px 0 0 8px",
                              borderColor: "#87C55F",
                              boxShadow: "0 2px 6px rgba(135, 197, 95, 0.15)",
                            }}
                          />
                          <Button
                            type="button"
                            style={{
                              height: "44px",
                              borderRadius: "0 8px 8px 0",
                              backgroundColor: "#87C55F",
                              border: "none",
                              padding: "0 24px",
                              fontWeight: "600",
                              boxShadow: "0 2px 6px rgba(135, 197, 95, 0.25)",
                            }}
                            onClick={addFeature}
                          >
                            Add
                          </Button>
                        </div>

                        {values.features.length > 0 && (
                          <div className="d-flex flex-wrap gap-2 mt-2">
                            {values.features.map((feat, idx) => (
                              <span
                                key={idx}
                                className="badge p-2 d-flex align-items-center gap-2"
                                style={{
                                  backgroundColor: "#87C55F",
                                  color: "#fff",
                                  fontSize: "13px",
                                  borderRadius: "6px",
                                  boxShadow: "0 2px 4px rgba(135, 197, 95, 0.2)",
                                }}
                              >
                                {feat}
                                <span
                                  style={{ cursor: "pointer", fontSize: "16px", fontWeight: "bold" }}
                                  onClick={() => removeFeature(idx)}
                                >
                                  &times;
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Clean Checkbox Cards */}
                      <div className="col-md-6 mb-1">
                        <div
                          className="p-2 px-3 rounded d-flex align-items-center gap-2"
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e0e0e0",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                            height: "44px",
                          }}
                        >
                          <Field
                            type="checkbox"
                            name="isPopular"
                            id="editIsPopularCheck"
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                              accentColor: "#87C55F",
                            }}
                          />
                          <label className="fw-semibold text-dark mb-0" htmlFor="editIsPopularCheck" style={{ cursor: "pointer", fontSize: "14px" }}>
                            Mark as Popular
                          </label>
                        </div>
                      </div>

                      <div className="col-md-6 mb-1">
                        <div
                          className="p-2 px-3 rounded d-flex align-items-center gap-2"
                          style={{
                            backgroundColor: "#f8f9fa",
                            border: "1px solid #e0e0e0",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                            height: "44px",
                          }}
                        >
                          <Field
                            type="checkbox"
                            name="isActive"
                            id="editIsActiveCheck"
                            style={{
                              width: "18px",
                              height: "18px",
                              cursor: "pointer",
                              accentColor: "#87C55F",
                            }}
                          />
                          <label className="fw-semibold text-dark mb-0" htmlFor="editIsActiveCheck" style={{ cursor: "pointer", fontSize: "14px" }}>
                            Is Active
                          </label>
                        </div>
                      </div>
                    </div>
                  </Modal.Body>

                  <Modal.Footer className="py-3 px-4">
                    <Button
                      type="submit"
                      disabled={submitting}
                      style={{
                        height: "44px",
                        backgroundColor: "#87C55F",
                        border: "none",
                        padding: "0 28px",
                        fontWeight: "600",
                        borderRadius: "8px",
                        boxShadow: "0 3px 8px rgba(135, 197, 95, 0.3)",
                      }}
                    >
                      Save Changes
                    </Button>
                  </Modal.Footer>
                </FormikForm>
              );
            }}
          </Formik>
        )}
      </Modal>

      {/* VIEW DETAILS MODAL */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#87C55F" }}>
            Subscription Plan Details
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-3 px-4">
          {detailsLoading ? (
            <Spinner />
          ) : selectedPlan ? (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom">
                <h5 className="mb-0">{selectedPlan.name}</h5>
                <div>
                  {selectedPlan.isPopular && (
                    <span className="badge bg-warning text-white me-2">Popular</span>
                  )}
                  <span
                    className={`badge ${selectedPlan.isActive ? "bg-success" : "bg-secondary"}`}
                  >
                    {selectedPlan.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div className="mb-3 p-3 bg-light rounded border d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted d-block fw-semibold">Razorpay Plan ID</small>
                  <code className="fs-6">{selectedPlan.razorpayPlanId || "N/A"}</code>
                </div>
                {selectedPlan.razorpayPlanId && (
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => handleCopyRazorpayId(selectedPlan.razorpayPlanId)}
                  >
                    Copy ID
                  </Button>
                )}
              </div>

              <div className="row g-3">
                <div className="col-md-4">
                  <small className="text-muted d-block">Price</small>
                  <strong style={{ color: "#87C55F" }} className="fs-5">
                    ₹{selectedPlan.amount} {selectedPlan.currency || "INR"}
                  </strong>
                </div>

                <div className="col-md-4">
                  <small className="text-muted d-block">Billing Cycle</small>
                  <strong>
                    Every {selectedPlan.interval > 1 ? selectedPlan.interval : ""} {selectedPlan.period}
                  </strong>
                </div>

                <div className="col-md-4">
                  <small className="text-muted d-block">Internal ID (_id)</small>
                  <code>{selectedPlan._id}</code>
                </div>

                <div className="col-12 mt-2">
                  <small className="text-muted d-block mb-1 fw-semibold">Description</small>
                  <p className="mb-0">{selectedPlan.description || "No description provided."}</p>
                </div>

                <div className="col-12 mt-2">
                  <small className="text-muted d-block mb-1 fw-semibold">Features</small>
                  {selectedPlan.features && selectedPlan.features.length > 0 ? (
                    <ul className="mb-0 ps-3">
                      {selectedPlan.features.map((f, i) => (
                        <li key={i}>{f}</li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-muted">No features listed</span>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </Modal.Body>

        <Modal.Footer className="py-2 px-4">
          <Button
            variant="secondary"
            onClick={() => setShowDetailsModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CREATED SUCCESS MODAL */}
      <Modal
        show={showCreatedSuccessModal}
        onHide={() => setShowCreatedSuccessModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ color: "#87C55F" }}>
            Plan Created Successfully
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="text-center py-4 px-4">
          <h5>"{createdPlan?.name}" is now live!</h5>
          <div className="p-3 bg-light rounded border my-3">
            <small className="text-muted d-block mb-1">Razorpay Plan ID</small>
            <code className="fs-5">{createdPlan?.razorpayPlanId || "N/A"}</code>
          </div>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => handleCopyRazorpayId(createdPlan?.razorpayPlanId)}
          >
            Copy Razorpay Plan ID
          </Button>
        </Modal.Body>

        <Modal.Footer className="py-2 px-4">
          <Button
            style={{ backgroundColor: "#87C55F", border: "none" }}
            onClick={() => setShowCreatedSuccessModal(false)}
          >
            Done
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE CONFIRMATION MODAL */}
      <Modal show={showDeleteModal} onHide={() => !submitting && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>

        <Modal.Body className="py-4 px-4">
          <p className="mb-1 fs-5 fw-semibold">
            Are you sure you want to delete <strong>{selectedPlan?.name}</strong>?
          </p>
          <small className="text-muted d-block mt-2">
            This delete the plan from the app. Existing subscribers are not cancelled. Razorpay plan cannot be deleted.
          </small>
        </Modal.Body>

        <Modal.Footer className="py-2 px-4">
          <Button
            style={{ backgroundColor: "#87C55F", border: "none" }}
            onClick={handleConfirmDelete}
            disabled={submitting}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Subscriptions;
